import { MineMembership, parseMineMembership } from '../../domain/value-objects/MineMembership';

/**
 * mineMembershipClaims — read the `mine_membership` claims off the stored access token.
 *
 * sprint-217 (epic-036 Phase 1 increment 1D). The MineMiner SPA has no `/me` endpoint, and
 * the login response carries `roles` but not memberships, so the claim on the token is where
 * the client area learns which mines to draw in its navigation.
 *
 * 🔴 RENDERING ONLY. This reads a token the client already holds; it verifies no signature
 * and it cannot know about a revocation that happened after the token was minted. Never gate
 * data access on what this returns — the server re-reads MineMembership rows on every
 * request (DR-005 clause 2). A membership that has been revoked will keep appearing here
 * until the token expires, and that is the intended, safe behaviour: the navigation shows a
 * stale mine, the API refuses its data.
 *
 * Deliberately hand-rolled rather than pulling in a JWT library: the app needs one unsigned
 * payload read, and a dependency whose name says "jwt" invites exactly the misuse the note
 * above warns about.
 */

const MINE_MEMBERSHIP_CLAIM = 'mine_membership';
const AUTH_TOKEN_STORAGE_KEY = 'authToken';

/**
 * Decode a base64url segment to a UTF-8 string.
 *
 * `atob` handles base64, not base64url, and does not restore padding — both of which JWT
 * segments need. The `escape`-free UTF-8 path matters because display names and mine names
 * are not ASCII-only.
 */
function decodeBase64Url(segment: string): string | null {
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/**
 * Read the claims object out of a JWT without verifying it.
 *
 * Returns null for anything that is not a three-segment token with a JSON object payload.
 */
export function readTokenClaims(token: string | null | undefined): Record<string, unknown> | null {
  if (!token) {return null;}

  const segments = token.split('.');
  if (segments.length !== 3) {return null;}

  // `noUncheckedIndexedAccess` is on, so the payload segment is `string | undefined` even
  // after the length check — narrow it explicitly rather than asserting.
  const payloadSegment = segments[1];
  if (payloadSegment === undefined) {return null;}

  const json = decodeBase64Url(payloadSegment);
  if (json === null) {return null;}

  try {
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {return null;}
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Extract the memberships from a decoded claims object.
 *
 * A JWT serialises a single-valued claim as a scalar and a multi-valued one as an array, so
 * both shapes are real and both appear in practice: a user who belongs to exactly one mine
 * produces the scalar form.
 */
export function membershipsFromClaims(
  claims: Record<string, unknown> | null,
): MineMembership[] {
  if (!claims) {return [];}

  const raw = claims[MINE_MEMBERSHIP_CLAIM];
  if (raw === undefined || raw === null) {return [];}

  const values = Array.isArray(raw) ? raw : [raw];

  return values
    .filter((v): v is string => typeof v === 'string')
    .map(parseMineMembership)
    .filter((m): m is MineMembership => m !== null);
}

/**
 * The memberships carried by the access token currently in localStorage.
 *
 * Returns an empty array when there is no token, when it is unreadable, or when it carries
 * no membership claims — a staff user's token legitimately carries none.
 */
export function readStoredMineMemberships(): MineMembership[] {
  if (typeof window === 'undefined') {return [];}

  const token = window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  return membershipsFromClaims(readTokenClaims(token));
}
